import { FC, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useIsSSO } from '~/hooks/useIsSSO';
import { useCurrentUser } from '~/queries/user/useUserQuery';
import { PlaceholderType } from './Placeholder';
import { RestrictionGuardType, UserRestriction, UserRoleRestriction } from '../types';
import { passesUserRestrictions } from '../utils';

const RealmAdminRouteGuard: FC<{ type?: RestrictionGuardType }> = (props) => {
  const { data: currentUser } = useCurrentUser({ enabled: false });

  const pass = currentUser?.sso_context?.realmAdmin ?? false;
  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};

export const UserRouteGuardSSO: FC<{
  restriction: UserRoleRestriction;
  type?: RestrictionGuardType;
}> = ({ restriction, ...props }) => {
  const { isSSO } = useIsSSO();

  const pass = useMemo(() => {
    if (restriction === 'sso' && !isSSO) return false;
    if (restriction === 'ssoRealmAdmin' && !isSSO) return false;
    if (restriction === 'base' && isSSO) return false;

    return true;
  }, [isSSO, restriction]);

  if (pass && restriction === 'ssoRealmAdmin') return <RealmAdminRouteGuard {...props} />;

  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};

export const UserRouteGuard: FC<{
  restrictions: readonly UserRestriction[];
  type?: RestrictionGuardType;
}> = ({ restrictions, ...props }) => {
  const { data: currentUser } = useCurrentUser({ enabled: false });

  const permissions = currentUser?.limit_overrides;
  const pass = useMemo(() => passesUserRestrictions({ restrictions, permissions }), [permissions]);

  return pass ? <Outlet /> : <PlaceholderType {...props} />;
};
